import axios from 'axios';

import { userEventEmitter } from '../eventEmitter';
import config from '../config';
const baseURL = `${config.api.host}:${config.api.port}/api/`;




const axiosInstance = axios.create({
	baseURL: baseURL,
	timeout: 5000,
	headers: {
		'Content-Type': 'application/json',
		accept: 'application/json',
	}, 
});

// seconds a token must still be valid for; one closer to expiring is renewed before it is sent
const EXPIRY_MARGIN = 10;

const tokenExpiry = (token) => {
	try {
		return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp;
	} catch {
		return 0;
	}
};

const usable = (token) => token && token !== 'undefined' && tokenExpiry(token) > Math.ceil(Date.now() / 1000) + EXPIRY_MARGIN;

// requests made at the same time share one renewal instead of each asking for its own
let renewal = null;
const renewAccessToken = () => {
	if (!renewal) {
		renewal = axios.post(baseURL + 'auth/token/refresh/', { refresh: localStorage.getItem('refresh_token') })
			.then(response => {
				localStorage.setItem('access_token', response.data.access);
				return response.data.access;
			})
			.finally(() => { renewal = null; });
	}
	return renewal;
};

axiosInstance.interceptors.request.use(
	async config => {
		let access = localStorage.getItem('access_token');
		// an expired token would only be answered with 401, so it is renewed first
		if (access && !usable(access) && usable(localStorage.getItem('refresh_token'))) {
			access = await renewAccessToken().catch(() => access);
		}
		config.headers['Authorization'] = access ? 'Bearer ' + access : null;
		return config;
	},
	error => {
		return Promise.reject(error);
	}
);

axiosInstance.interceptors.response.use(response => {
    return response;
}, async error => {
    
    const originalRequest = error.config;

    // Utility function to handle user logout
    const handleLogout = () => {
		
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        userEventEmitter.emit('logout');
        window.location.href = '/login/';
    }

    if (typeof error.response === 'undefined') {
        // Handle network error here if needed
        return Promise.reject(error);
    }

    if (error.response.status === 401 && originalRequest.url === baseURL + 'auth/token/refresh/') {
        handleLogout();
        return Promise.reject(error);
    }

    // a request already repeated with a renewed token is not repeated again
    if (error.response.data.code === 'token_not_valid' && 
        error.response.status === 401 &&
        !originalRequest._retried
    ) {
        originalRequest._retried = true;
        
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (refreshToken && refreshToken !== "undefined") {
            const tokenParts = JSON.parse(atob(refreshToken.split('.')[1]));
            const now = Math.ceil(Date.now() / 1000);

            if (tokenParts.exp > now) {
                // the request interceptor sends the renewed token with the repeated request
                // only a failed renewal logs out; the repeated request answers for itself
                return renewAccessToken()
                    .then(() => axiosInstance(originalRequest), () => {
                        handleLogout();
                        return Promise.reject(error);
                    });
            } else {
                console.log('Refresh token is expired', tokenParts.exp, now);
                handleLogout();
            }
        } else {
            console.log('Refresh token not available.');
            handleLogout();
        }
    }

    return Promise.reject(error);
});

export default axiosInstance;